import { useCallback, useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';

import HttpClient from '../../shared/infrastructures/clients/http.client';
import { extractTranslatedApiErrorMessage } from '../../shared/utils/translate-api-error.util';
import { GetSegmentsFilters, GetSegmentsParams, GetSegmentsResponse, Segment } from '../types/segment.type';
import { ADMIN_SEGMENTS_API_PATHS } from '../constants/segments-api-paths.constants';

export type GetSegmentsRequest = Omit<GetSegmentsParams, 'filters'> & { filters: GetSegmentsFilters };

type UseAdminGetSegmentsResult = {
  isLoading: boolean;
  isRefetching: boolean;
  totalCount: number;
  segments: Segment[];
  refetch: () => Promise<void>;
};

const INITIAL_STATE: Omit<UseAdminGetSegmentsResult, 'refetch'> = {
  isLoading: true,
  isRefetching: false,
  totalCount: 0,
  segments: [],
};

export function useAdminGetSegments({
  page,
  limit,
  sortBy,
  sortDirection,
  filters,
}: GetSegmentsRequest): UseAdminGetSegmentsResult {
  const { enqueueSnackbar } = useSnackbar();
  const [result, setResult] = useState(INITIAL_STATE);

  const serializedFilters = JSON.stringify(filters);

  const refetch = useCallback(async (): Promise<void> => {
    setResult((previous) => (previous.isLoading ? previous : { ...previous, isRefetching: true }));

    try {
      const response = await HttpClient.get<GetSegmentsResponse>(ADMIN_SEGMENTS_API_PATHS.GET_SEGMENTS, {
        params: { page, limit, sortBy, sortDirection, filters: serializedFilters },
      });

      setResult({
        isLoading: false,
        isRefetching: false,
        totalCount: response.totalCount,
        segments: response.segments,
      });
    } catch (error) {
      enqueueSnackbar(
        extractTranslatedApiErrorMessage(error, 'Une erreur est survenue lors de la récupération des segments'),
        { variant: 'error' }
      );
      setResult({ isLoading: false, isRefetching: false, totalCount: 0, segments: [] });
    }
  }, [page, limit, sortBy, sortDirection, serializedFilters, enqueueSnackbar]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { ...result, refetch };
}
